import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  ErrorCode,
  EventNotFound,
  ReservationRedeemRefused,
  StoreNotFound,
  type StoreRoleForbidden,
  TableClaimRefused,
  TableKindNotFound,
  TableOrderNotFound,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { ClaimedTable, TablePayment, VendorShows } from "../model/vendor-show";
import { fail, forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

/**
 * `/stores/:storeId/shows`: other shops' card shows, as a shop that might sell at one
 * sees them, and taking a table there, held for five minutes and paid in the app. Every
 * member reads them; taking, paying for or giving up a table needs a Manager.
 */
export class VendorShowsApi {
  constructor(private readonly t: Transport) {}

  async list(ctx: ApiContext, storeId: string): Promise<Result<VendorShows, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/shows`);
    return forward(
      r,
      (j) => parse(VendorShows, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Holds the lowest-numbered free table of the kind for five minutes, with its payment
   * (null for a table that costs nothing, which is the shop's at once). The table is the
   * shop's once paid, at its booth on the show's venue. One per show.
   */
  async claimTable(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    kindId: string,
  ): Promise<
    Result<
      ClaimedTable,
      ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound | TableKindNotFound | TableClaimRefused
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    if (!kindId) return fail(new TableKindNotFound(kindId));
    const r = await this.t.post(ctx, `/stores/${storeId}/shows/${eventId}/tables/claim`, { body: { kindId } });
    return forward(
      r,
      (j) => parse(ClaimedTable, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.code === ErrorCode.DoesntExist) {
            if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
            if (e.incorrectValue === "kindId") return new TableKindNotFound(kindId, e);
          }
          if (e.incorrectValue === "eventId" || e.incorrectValue === "kindId") {
            return new TableClaimRefused(e.detailedDesc, e);
          }
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * The payment for a table the shop holds: the same intent each time while the hold lasts.
   * `TableClaimRefused` once the order no longer waits for payment.
   */
  async payment(
    ctx: ApiContext,
    storeId: string,
    orderId: string,
  ): Promise<
    Result<TablePayment, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | TableOrderNotFound | TableClaimRefused>
  > {
    if (!orderId) return fail(new TableOrderNotFound(orderId));
    const r = await this.t.post(ctx, `/stores/${storeId}/shows/orders/${orderId}/payment`);
    return forward(
      r,
      (j) => parse(TablePayment, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "orderId") {
            return e.code === ErrorCode.DoesntExist
              ? new TableOrderNotFound(orderId, e)
              : new TableClaimRefused(e.detailedDesc, e);
          }
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Puts a table reserved on the storefront for an email on this shop, by its code: the
   * shop's from now on, at its booth on the show's venue. Again at the same shop is no fault.
   */
  async redeemReservation(
    ctx: ApiContext,
    storeId: string,
    code: string,
  ): Promise<Result<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | ReservationRedeemRefused>> {
    const r = await this.t.post(ctx, `/stores/${storeId}/shows/reservations/redeem`, { body: { code } });
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "code") return new ReservationRedeemRefused(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Gives up a table before paying for it: the order ends, its payment is voided and the table goes back on sale. */
  async cancelOrder(
    ctx: ApiContext,
    storeId: string,
    orderId: string,
  ): Promise<
    Result<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | TableOrderNotFound | TableClaimRefused>
  > {
    if (!orderId) return fail(new TableOrderNotFound(orderId));
    const r = await this.t.post(ctx, `/stores/${storeId}/shows/orders/${orderId}/cancel`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "orderId") {
            return e.code === ErrorCode.DoesntExist
              ? new TableOrderNotFound(orderId, e)
              : new TableClaimRefused(e.detailedDesc, e);
          }
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }
}
